import { Link, useNavigate } from '@tanstack/react-router';
import { useCriterios } from '@/api/criterios-rateio';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { BotaoRelatorios } from '@/relatorios';
import { formatarContagem } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { CriterioDetalhe, type BuscaCriterios } from './criterio-detalhe';
import { BASE_ROTULO } from './criterios-form';

/** `validateSearch`: so aceita um id bem formado ("novo" inclusive). */
export function lerBuscaCriterios(bruto: Record<string, unknown>): BuscaCriterios {
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return id ? { id } : {};
}

/** Tela "Critérios de rateio" (/custos/criterios): lista e registro por `?id=`. Leitura: centro.ver; gravar: centro.editar. */
export function CriteriosPage({ busca }: { busca: BuscaCriterios }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeVer = pode('centro.ver');
  const podeEditar = pode('centro.editar');
  const lista = useCriterios();
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os critérios.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os critérios" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando critérios…</Mensagem>;
  else if (itens.length === 0) {
    corpo = (
      <Mensagem papel="status">
        Nenhum critério cadastrado.
        {podeEditar && (
          <>
            {' '}
            <Link to="/custos/criterios" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Crie o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Lista de critérios de rateio">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Identificador</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Descrição</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Base</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Centros</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((c) => (
              <tr
                key={c.id}
                data-testid="linha-criterio"
                onClick={() => void navigate({ to: '/custos/criterios', search: { id: c.id } })}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === c.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">
                  <Link to="/custos/criterios" search={{ id: c.id }} className="text-ink no-underline hover:underline" onClick={(e) => e.stopPropagation()}>
                    {c.identificador}
                  </Link>
                </td>
                <td className="px-3 py-3 text-ink-secondary">{c.descricao}</td>
                <td className="px-3 py-3 text-ink-secondary">{BASE_ROTULO[c.base]}</td>
                <td className="tabular px-3 py-3 text-ink-secondary">{c.todosCentros ? 'Todos' : formatarContagem(c.centros.length)}</td>
                <td className="px-4 py-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', c.ativo ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                    {c.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Critérios de rateio
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Critérios de rateio</h1>
        </div>
      </div>

      <section aria-label="Lista de critérios" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {corpo}
        <div className="border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-criterios">
          {lista.data ? `${formatarContagem(itens.length)} critério${itens.length === 1 ? '' : 's'}` : ' '}
        </div>
      </section>

      {busca.id === 'novo' && !podeEditar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar critérios de rateio.
        </section>
      ) : (
        busca.id && <CriterioDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} podeEditar={podeEditar} podeVerCentros={podeVer} />
      )}

      {/* Com um critério aberto, a barra de registro (Salvar) ocupa o rodapé: a barra da lista sairia por cima dela. */}
      {!busca.id && (
        <BarraDeAcoesLista
          novo={podeEditar ? { rotulo: 'Novo critério', onClick: () => void navigate({ to: '/custos/criterios', search: { id: 'novo' } }) } : undefined}
          paginacao={lista.data ? pagItens.paginacao : undefined}
          relatorios={<BotaoRelatorios tela="criterios" />}
        />
      )}
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
